import React, {useState, useRef} from "react";
import "./Home.css";
import {Link} from "react-router-dom";
import company from "../../Assets/svg/company.svg";
import {Helmet} from "react-helmet-async";
import {
    Building2,
    Globe2,
    Building,
    ArrowRight,
    Star,
    Award,
    ChevronRight,
    CheckCircle,
    Globe,
    CreditCard,
} from "lucide-react";
import { motion, useScroll } from "framer-motion"
import imagetry from "../../Assets/Images/pexels-fauxels-3184292.webp";
import ParallaxBusinessSetup from "./parallax";

const PremiumCard = ({
                         title,
                         price,
                         features,
                         additionalFeatures,
                         recommended = false,
                         primaryColor = 'from-[#74ACDF]',
                         secondaryColor = 'to-[#4F46E5]',
                         accentColor = 'yellow'
                     }) => {
    return (
        <div className="relative group h-full">
            {recommended && (
                <div
                    className={`
            absolute top-24 right-2 z-20 
            transform rotate-45 
            bg-${accentColor}-500 
            text-[#74ACDF]
            px-8 py-2 
            shadow-xl
            text-xs 
            font-bold 
            tracking-wider
            origin-bottom-right
            transition-all duration-300
            group-hover:scale-110
          `}
                >
                    <Star className="inline-block mr-2 w-4 h-4 -mt-0.5"/>
                    BEST OPTION
                </div>
            )}

            <div
                className={`
          absolute -inset-0.5 
          bg-gradient-to-br 
          ${primaryColor} ${secondaryColor}
          opacity-0 
          group-hover:opacity-100 
          transition-opacity 
          duration-500 
          blur-lg 
          animate-pulse
        `}
            />

            <div
                className={`
          relative 
          border border-gray-200
          bg-white 
          rounded-lg 
          overflow-hidden 
          shadow-md
          transform 
          transition-all 
          duration-500 
          group-hover:scale-[1.02]
          group-hover:shadow-2xl
          ${recommended ? `ring-2 ring-${accentColor}-300` : ''}
          h-full
          flex flex-col
        `}
            >
                <div
                    className={`
            absolute top-0 left-0 w-full h-1.5 
            bg-gradient-to-r 
            ${primaryColor} ${secondaryColor}
          `}
                />

                <div className="p-6 flex flex-col flex-grow">
                    <div className="flex justify-between items-center mb-4">
                        <h3
                            className={`
                text-3xl font-bold 
                bg-gradient-to-r 
                ${primaryColor} ${secondaryColor}
                bg-clip-text text-transparent
                uppercase
                tracking-tight
              `}
                        >
                            {title}
                        </h3>
                        <Award
                            className={`
                w-10 h-10 
                text-transparent 
                bg-gradient-to-r 
                ${primaryColor} ${secondaryColor}
                bg-clip-text
                opacity-70
              `}
                        />
                    </div>

                    <p className="text-gray-500 text-xs mb-1">From</p>
                    <p
                        className={`
              text-5xl font-extrabold mb-6 
              bg-gradient-to-r 
              ${primaryColor} ${secondaryColor}
              bg-clip-text text-transparent
              tracking-tighter
            `}
                    >
                        {price} AED
                    </p>

                    <div className="space-y-4 mb-6 flex-grow">
                        <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-lg font-semibold mb-3 flex items-center">
                                <CheckCircle className="mr-2 text-green-500"/>
                                Key Benefits
                            </h4>
                            <ul className="space-y-2">
                                {features.map((feature, index) => (
                                    <li
                                        key={index}
                                        className="flex items-start group"
                                    >
                                        <svg
                                            className={`
                        h-5 w-5 mr-3 
                        text-transparent 
                        bg-gradient-to-r 
                        ${primaryColor} ${secondaryColor}
                        bg-clip-text 
                        group-hover:scale-110 
                        transition-transform
                      `}
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                        <span
                                            className="text-gray-700 text-sm group-hover:text-blue-600 transition-colors">
                      {feature}
                    </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-lg font-semibold mb-3 flex items-center">
                                <Globe className="mr-2 text-blue-500"/>
                                Additional Benefits
                            </h4>
                            <ul className="space-y-2">
                                {additionalFeatures.map((feature, index) => (
                                    <li
                                        key={index}
                                        className="flex items-start group"
                                    >
                                        <CreditCard
                                            className={`
                        h-5 w-5 mr-3 
                        text-transparent 
                        bg-gradient-to-r 
                        ${primaryColor} ${secondaryColor}
                        bg-clip-text 
                        group-hover:scale-110 
                        transition-transform
                      `}
                                        />
                                        <span
                                            className="text-gray-700 text-sm group-hover:text-blue-600 transition-colors">
                      {feature}
                    </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="mt-auto space-y-3">
                        <Link
                            to="/pricing"
                            className={`
                w-full py-2.5 px-5 
                rounded-md 
                bg-gradient-to-r 
                ${primaryColor} ${secondaryColor}
                text-white 
                font-semibold 
                flex items-center 
                justify-center 
                hover:opacity-90 
                transition-all 
                group
                text-sm
                uppercase
                tracking-wider
                mb-2
              `}
                        >
                            View Details
                            <ChevronRight
                                className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform"
                            />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

const businessTypes = {
    "Real Estate and Construction": {
        icon: company, // Asegúrate de importar los íconos correctos
        description:
            "Set up your real estate or construction business with exceptional benefits including tax advantages and streamlined permit processes.",
    },
    "Tourism and Hospitality": {
        icon: company,
        description:
            "Establish your tourism business with premium locations and exclusive hospitality sector benefits in Dubai's prime areas.",
    },
    "Technology and Innovation": {
        icon: company,
        description:
            "Launch your tech venture with access to cutting-edge infrastructure and innovation-friendly policies in Dubai's tech hubs.",
    },
    "Financial Services": {
        icon: company,
        description:
            "Start your financial services firm with world-class infrastructure and regulatory framework advantages in Dubai.",
    },
};

export default function Home() {

    const [activeStep, setActiveStep] = useState(null);
    const containerRef = useRef(null);
    const {scrollYProgress} = useScroll({
        target: containerRef,
        offset: ["start start", "end end"]
    });

    const handleLearnMore = (businessType) => {
        const encodedType = encodeURIComponent(businessType);
        return `/dubai-work-zones?type=${encodedType}`;
    };

    const zoneData = [
        {
            title: 'Sharjah Free Zone',
            price: '5,500*',
            features: [
                "Inclusive of Office Lease Agreement for Banking purposes",
                "Inclusive of 3 - 5 Activities",
                "Multiple shareholders permitted",
            ],
            additionalFeatures: [
                "Fast registration in less than 3 days",
                "Specialized legal support",
                "Access to a local business network",
                "Assistance with banking procedures"
            ]
        },
        {
            title: 'Dubai Free Zone',
            price: '12,500*',
            recommended: true,
            features: [
                "Upgrade to visa package at a minimal cost of AED 1,850",
                "Free desk space included in the package",
                "Prestigious Dubai address for your company",
                "Extendable to unlimited visa options",
            ],
            additionalFeatures: [
                "Networking with international companies",
                "Business certifications",
                "Access to investment events",
                "Business expansion consultancy"
            ],
            accentColor: 'yellow'
        },
        {
            title: 'Dubai Mainland',
            price: '13,000*',
            features: [
                "LLC Licenses with 100% ownership",
                "No paid-up share capital required",
                "Business license within a day",
                "Up to 10 activities on a single license",
            ],
            additionalFeatures: [
                "Comprehensive legal support",
                "Connections with key sectors",
                "Business marketing services",
                "Personalized tax advisory"
            ]
        }
    ];


    const blogData = [
        {
            to: '/how-to-create-a-business-in-dubai',
            imageUrl: imagetry,
            date: '10 - 25 - 2024',
            title: 'How Can I Start My Business In Dubai?'
        },
        {
            to: '/how-to-maximize-your-real-state-investments-in-dubai',
            imageUrl: imagetry,
            date: '11 - 02 - 2024',
            title: 'How to maximize your real state investments in Dubai?'
        },
        {
            to: '/why-dubai-is-the-ultimate-destination-for-entrepreneurs-in-2024',
            imageUrl: imagetry,
            date: '11 - 15 - 2024',
            title: 'Why Dubai is the Ultimate Destination for Entrepreneurs in 2024'
        }
    ];

    const options = [
        {
            title: "Freezone",
            icon: <Building2 size={40} className="text-[#74ACDF]"/>,
            benefits: [
                "Retain 100% ownership of your Dubai company",
                "No tax on properties",
                "No custom duties",
                "Option to repatriate 100% of your capital and profits",
            ],
            linkText: "Find my Freezone",
        },
        {
            title: "Mainland",
            icon: <Building size={40} className="text-[#74ACDF]"/>,
            benefits: [
                "Business activities not limited",
                "Operate anywhere inside and outside the country",
                "No limit on number of visas",
            ],
            linkText: "Find my Mainland",
        },
        {
            title: "Offshore",
            icon: <Globe2 size={40} className="text-[#74ACDF]"/>,
            benefits: [
                "Business activities not limited",
                "Operate anywhere inside and outside the country",
                "No limit on number of visas",
            ],
            linkText: "Find my Offshore",
        },
    ];

    return (
        <div>
            <Helmet>
                <meta name="keywords" content="Setup Business in Dubai, Business Setup, Business Solutions in Dubai "/>
                <title>Business Solutions Dubai - Setup Your Company in Dubai Now</title>
                <meta name="description"
                      content="Business Solutions Dubai offers tailored business zones designed to accelerate your growth. From tech innovation to real estate, Dubai offers unparalleled opportunities for global entrepreneurs."/>
                <meta name="robots" content="index, follow"/>
                <meta name="lenguage" content="english"/>
                <meta name="publisher" content="Business Solutions Dubai"/>
                <link rel="canonical" href="https://businesssolutionsdubai.com/"/>
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Organization",
                        "name": "Business Solutions Dubai",
                        "url": "https://www.tu-sitio.com",
                        "logo": "https://www.tu-sitio.com/logo.png",
                        "contactPoint": {
                            "@type": "ContactPoint",
                            "telephone": "+971-123-456-789",
                            "contactType": "Customer Service",
                            "areaServed": "AE",
                            "availableLanguage": ["en", "ar"]
                        },
                        "sameAs": [
                            "https://www.facebook.com/tuempresa",
                            "https://www.twitter.com/tuempresa",
                            "https://www.linkedin.com/company/tuempresa"
                        ],
                        "description": "Especialistas en globalización de empresas en Dubái. Ofrecemos servicios de asesoría para establecer compañías en zonas francas, mainland y offshore, con experiencia en todos los procesos legales y financieros.",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Business Bay, Burj Khalifa Street",
                            "addressLocality": "Dubai",
                            "postalCode": "00000",
                            "addressCountry": "AE"
                        },
                        "areaServed": "AE",
                        "offers": {
                            "@type": "Offer",
                            "url": "https://www.tu-sitio.com/servicios",
                            "priceCurrency": "USD",
                            "category": [
                                "Zonas Francas",
                                "Mainland",
                                "Offshore"
                            ],
                            "itemOffered": {
                                "@type": "Service",
                                "name": "Asesoría para establecer empresas en Dubái",
                                "description": "Servicios de consultoría para la globalización de negocios, incluyendo registro de empresas en zonas francas y mainland."
                            }
                        }
                    })}
                </script>
            </Helmet>
            <motion.section
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{duration: 0.8}}
                className="relative h-[1100px] md:h-[100vh] pt-0 md:pt-60 pd-4 md:pb-20 flex items-center bg-image"
            >
                <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
                    <motion.div
                        initial={{x: -100, opacity: 0}}
                        animate={{x: 0, opacity: 1}}
                        transition={{duration: 0.8, delay: 0.2}}
                        className="space-y-6 z-10"
                    >
                        <h1 className="text-3xl md:text-6xl font-extrabold bg-gray-800 text-white p-4 rounded-md">
  <span className="bg-gradient-to-r from-[#74ACDF] to-blue-600 bg-clip-text text-transparent">
    <i>Signature</i> All-Inclusive
  </span>
                            <br/>
                            <span>Business Setup Package</span>
                        </h1>


                        <motion.p
                            initial={{opacity: 0}}
                            animate={{opacity: 1}}
                            transition={{duration: 0.6, delay: 0.4}}
                            className="text-lg md:text-xl text-gray-200 leading-relaxed md:w-[70%] w-[90%] text-shadow-lg"
                        >
                            Are you seeking a comprehensive business setup package that covers
                            all the essentials for your venture, no matter where you're
                            located—whether in India, the United Kingdom, the United States, or
                            any other country?
                        </motion.p>


                        <Link to="/pricing">
                            <button
                                className="group relative overflow-hidden rounded-3xl bg-blue-600 px-8 py-4 my-8 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                                <div
                                    className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-400 opacity-0 transition-opacity group-hover:opacity-100"/>
                                <span
                                    className="relative flex items-center justify-center text-xl font-semibold text-white">
                    View Offer
                    <svg
                        className="ml-2 h-6 w-6 transition-transform group-hover:translate-x-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                      <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </span>
                            </button>
                        </Link>
                    </motion.div>
                </div>
            </motion.section>

            <section className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                <h2 className="flex justify-center align-center text-center pb-16 text-4xl md:text-6xl font-bold gradient-text">Pricing
                    and Services</h2>
                <div className="grid md:grid-cols-3 gap-8">
                    {zoneData.map((zone, index) => (
                        <PremiumCard
                            key={index}
                            {...zone}
                        />
                    ))}
                </div>
            </section>

            <section className="flex flex-col items-center justify-center w-full h-auto px-4 pb-16">
                <h2 className="gradient-text text-3xl md:text-6xl w-[90%] md:w-[70%] text-center font-bold">
                    Already Have A Business?
                </h2>
                <p className="py-10 text-center w-[90%] md:w-[70%] text-mg">
                    If you already have a business and you don't know where to start, we
                    can help you with the setup and maintenance of your business. We can
                    also help you with the taxes and companies.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-7xl">
                    {Object.entries(businessTypes).map(([type, data]) => (
                        <div
                            key={type}
                            className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                        >
                            <div className="w-12 h-12 bg-[#74ACDF] flex justify-center items-center rounded-md">
                                <img src={data.icon} alt={type} className="w-10 h-10" loading="lazy"/>
                            </div>

                            <h3 className="py-4 font-extrabold text-lg">{type}</h3>

                            <p className="text-gray-700 py-4 min-h-[80px]">
                                {data.description}
                            </p>

                            <Link
                                to={handleLearnMore(type)}
                                className="inline-flex items-center font-extrabold text-sm text-[#74ACDF] hover:text-blue-700 transition-colors duration-300"
                            >
                                <span className="underline">Verify Your Business</span>
                                <svg
                                    className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform duration-300"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 5l7 7-7 7"
                                    />
                                </svg>
                            </Link>
                        </div>
                    ))}
                </div>
                <Link
                    to="/dubai-work-zones"
                    className="w-[100%] md:w-[30%] mt-8 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center space-x-2 group"
                >
                    <span>Find More Companies Here</span>
                    <svg
                        className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                        />
                    </svg>
                </Link>
            </section>
            <section
                className="bg-[#292929] pt-0 md:py-20 px-4 flex justify-around items-center flex-col md:flex-row bg-image-2 h-[1400px] md:h-[100vh]">
                <div className="max-w-4xl mx-auto flex flex-col items-center w-full md:w-[40%]">
                    {/* Header */}
                    <div className="relative mb-12 w-full">
                        <div
                            className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-[#74ACDF] to-[#4F46E5]"/>
                        <h2 className="text-white text-4xl text-center font-bold leading-tight">
                            How Much Does Business Setup In Dubai Cost?
                        </h2>
                    </div>

                    {/* Main content */}
                    <div className="relative mb-16 w-full">
                        <div
                            className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-[#74ACDF] to-[#4F46E5] opacity-50"/>
                        <p className="text-gray-300 text-lg text-center leading-relaxed">
                            We begin by conducting a thorough verification of your business.
                            Following this, we will provide you with a comprehensive quote for
                            the setup, including a detailed cost breakdown. Additionally, we
                            offer a complimentary consultation to discuss your business setup
                            requirements.
                        </p>
                        <div className="mt-8 flex justify-center gap-8 text-2xl">
                            <div className="text-center">
                                <span className="block text-[#74ACDF] font-bold">From</span>
                                <span className="text-white italic">$5,900 AED</span>
                            </div>
                        </div>
                    </div>

                    {/* Tagline */}
                    <h3 className="text-2xl text-center font-bold mb-12 max-w-2xl">
            <span className="bg-gradient-to-r from-[#74ACDF] to-[#4F46E5] bg-clip-text text-transparent">
              Follow our way to globalize your business on Dubai
            </span>
                        <span className="block mt-2 text-white italic opacity-90">
              The Best Option for Doing Business!
            </span>
                    </h3>

                    {/* CTA Button */}
                    <a
                        href="https://wa.link/txunpz"
                        className="group relative px-8 py-4 overflow-hidden rounded-lg bg-gradient-to-r from-[#74ACDF] to-[#4F46E5] transition-all duration-300 hover:scale-105"
                    >
                        <div
                            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-[#4F46E5] to-[#74ACDF]"/>
                        <span className="relative text-white font-medium text-lg">
              Contact an Agent
            </span>
                    </a>
                </div>
                <div className="w-full md:w-[50%] flex justify-center items-center">

                </div>
            </section>

            <section className="container mx-auto px-4 mb-6 py-16 max-w-7xl">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 gradient-text">
                        What Is The Best Option For My Business?
                    </h2>
                    <p className="text-lg max-w-4xl mx-auto text-gray-600">
                        The best choice depends on your <span className="italic">business needs</span>, including
                        whether you choose a <span className="italic">Freezone</span> or <span
                        className="italic">mainland</span>, as well as factors like the number of visas or shareholders
                        required. We offer a variety of tailored packages to suit your goals. <br/>{" "}
                        <strong>Explore our packages</strong>
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                    {options.map((option, index) => (
                        <div
                            key={index}
                            className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 p-8 flex flex-col items-center"
                        >
                            <div className="mb-6">{option.icon}</div>
                            <h3 className="text-2xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-[#74ACDF] to-[#437CAD] bg-clip-text text-transparent">
                                {option.title}
                            </h3>
                            <ul className="space-y-4 mb-8 w-full">
                                {option.benefits.map((benefit, idx) => (
                                    <li key={idx} className="flex items-start text-gray-600">
                                        <span className="mr-2 text-[#74ACDF]">•</span>
                                        <span>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link
                                to="/zones-info-dubai"
                                className="mt-auto px-6 py-3 border-2 border-[#74ACDF] text-[#74ACDF] rounded-lg font-semibold hover:bg-[#74ACDF] hover:text-white transition-colors duration-300"
                            >
                                {option.linkText}
                            </Link>
                        </div>
                    ))}
                </div>

                <div className="text-center">
                    <Link
                        to="/zones-info-dubai"
                        className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-[#74ACDF] to-[#437CAD] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity duration-300"
                    >
                        See Zones
                    </Link>
                </div>
                {/*<div className="flex justify-center align-center">*/}
                {/*    <img src={trial2} alt="dubai-landscape" className="md:w-[70%] w-[100%] my-16 rounded-lg"/>*/}
                {/*</div>*/}
            </section>

            <div className="w-full bg-blue-600 py-36 bg-trial">
                <div className="max-w-4xl mx-auto px-6 text-center text-white">
                    <h2 className="text-3xl font-bold mb-6">Ready to Start Your Business in Dubai?</h2>
                    <p className="text-xl mb-8">Let our experts guide you through every step of the process</p>
                    <a
                        href="https://wa.link/txunpz"
                        className="w-[220px] bg-white text-blue-600 px-8 py-3 rounded-full hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 mx-auto"
                    >

                        Contact Us Now
                        <ArrowRight className="h-5 w-5"/>
                    </a>
                </div>
            </div>

            <ParallaxBusinessSetup/>
            <section className="flex justify-center items-center flex-col">
                <h2 className="text-3xl w-[80%] text-center font-bold py-8">
                    Check Our Information
                </h2>
                <div className="flex flex-wrap justify-center align-middle w-full">
                    {blogData.map((blog, index) => (
                        <div key={index}
                             className="my-8 w-[300px] m-8 bg-white rounded-lg shadow-lg overflow-hidden relative group transition-all duration-300 hover:shadow-xl">
                            <Link to={blog.to} className="block w-full h-full">
                                <div className="relative w-full h-64">
                                    <div
                                        className="absolute top-4 right-4 bg-white/90 px-3 py-1 rounded-full text-sm font-medium text-gray-700 backdrop-blur-sm z-10">
                                        <p>{blog.date}</p>
                                    </div>
                                    <div
                                        className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                                    <img src={blog.imageUrl} alt={blog.title} className="w-full h-full object-cover"
                                         loading="lazy"/>
                                </div>
                                <div className="p-6">
                                    <h3 className="text-2xl font-bold mb-4 gradient-text">{blog.title}</h3>
                                    <div
                                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300">
                                        See More
                                        <svg
                                            className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300"
                                            fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M9 5l7 7-7 7"/>
                                        </svg>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
